/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { Button } from '@mui/material';
import DefaultModal from './DefaultModal';
import { useOneTimePaymentContext } from '../../contexts/OneTimePaymentContext';
import Styles from '../../css/OneTimePaymentModal.module.css';
import dayjs from 'dayjs';
import { numericFormatter } from 'react-number-format';
import { useEffect, useState } from 'react';
import { useViewingContext } from '../../contexts/ViewingContext';

type oneTimePaymentModalProps = {
  content: string;
};

const OneTimePaymentModal = (props: oneTimePaymentModalProps) => {
  const oneTimePaymentContext = useOneTimePaymentContext();
  const { isServicerView } = useViewingContext();
  const [pageNumber, setPageNumber] = useState(1);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const deleteRow = oneTimePaymentContext.deleteRow;

  const closeConfirmModal = () => {
    oneTimePaymentContext.setOneTimePaymentModalOpen(false);
    setPageNumber(1);
    setIsAuthorized(false);
  };

  useEffect(() => {
    if (!oneTimePaymentContext.oneTimePaymentModalOpen) {
      setPageNumber(1);
      setIsAuthorized(false);
    }
  }, [oneTimePaymentContext.oneTimePaymentModalOpen]);

  if (props.content === 'create') {
    return (
      <DefaultModal
        className={Styles.confirmPaymentModal}
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={closeConfirmModal}
        title={<h1>Confirm Payment Details</h1>}
        content={
          pageNumber === 1 ? (
            <div className={`${Styles.modalContainer} ${Styles.confirmPaymentContent} ${Styles.certificationStep}`}>
              <div>Do you certify you are an authorized signer on the bank account you have provided today?</div>
              <label>
                <input type='checkbox' checked={isAuthorized} onChange={() => setIsAuthorized((prev) => !prev)} />
                Confirm?
              </label>
            </div>
          ) : (
            <div className={`${Styles.modalContainer} ${Styles.confirmPaymentContent}`}>
              <div className={Styles.disclosureText}>
                <p>
                  If the account information provided is not accurate or if this payment gets returned by your bank as
                  "unable to locate", "invalid account number", insufficient or uncollected funds, Sun West Mortgage
                  will not be held responsible for any fee(s) assessed or negative credit reporting, and all future
                  dated electronic payments may be deleted and not processed.
                </p>
                <br />
              </div>
              <div className={Styles.paymentDetailsGroup}>
                <p className={Styles.paymentDetailsLabel}>Payment Details</p>
                <div className={Styles.paymentDetailsPanel}>
                  <div className={Styles.gridContainer}>
                    <p className={Styles.label}>Transit Routing No: </p>
                    <p>{oneTimePaymentContext.autopayInfo.otpRoutingNo}</p>
                    <p className={Styles.label}>Bank Name: </p>
                    <p>{oneTimePaymentContext.autopayInfo.otpBankName}</p>
                    <p className={Styles.label}>Bank Account No: </p>
                    <p>{oneTimePaymentContext.autopayInfo.otpBankAccountNo}</p>
                    <p className={Styles.label}>Bank Account Type: </p>
                    <p>
                      {oneTimePaymentContext.otpBankAcctTypeQidInfo.find(
                        (option) => option.keyword === oneTimePaymentContext.autopayInfo.otpBankAccountTypeKeyword,
                      )?.description ?? '—'}
                    </p>
                    <p className={Styles.label}>Due Date: </p>
                    <p>{dayjs.unix(oneTimePaymentContext.paymentInfo?.dueDate).format('MM/DD/YYYY')}</p>
                    {!oneTimePaymentContext.removePayment && (
                      <>
                        <p className={Styles.label}>Payment: </p>
                        <p>
                          {numericFormatter(oneTimePaymentContext.otpInfo.payment.toString(), {
                            thousandSeparator: true,
                            prefix: '$',
                            decimalScale: 2,
                            fixedDecimalScale: true,
                          })}
                        </p>
                      </>
                    )}
                    <p className={Styles.label}>Additional Principal: </p>
                    <p>
                      {numericFormatter(oneTimePaymentContext.otpInfo.additionalPrincipal.toString(), {
                        thousandSeparator: true,
                        prefix: '$',
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      })}
                    </p>
                    <p className={Styles.label}>Additional Escrow: </p>
                    <p>
                      {numericFormatter(oneTimePaymentContext.otpInfo.additionalEscrow.toString(), {
                        thousandSeparator: true,
                        prefix: '$',
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      })}
                    </p>
                    <p className={Styles.label}>Late Charges: </p>
                    <p>
                      {numericFormatter(oneTimePaymentContext.otpInfo.lateCharges.toString(), {
                        thousandSeparator: true,
                        prefix: '$',
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      })}
                    </p>
                    <p className={Styles.label}>Corporate Advance: </p>
                    <p>
                      {numericFormatter(oneTimePaymentContext.otpInfo.corporateAdvance.toString(), {
                        thousandSeparator: true,
                        prefix: '$',
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      })}
                    </p>
                    <p className={Styles.label}>Assessments: </p>
                    <p>
                      {numericFormatter(oneTimePaymentContext.otpInfo.assessments.toString(), {
                        thousandSeparator: true,
                        prefix: '$',
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      })}
                    </p>
                    <p className={Styles.label}>Suspense: </p>
                    <p>
                      {numericFormatter(oneTimePaymentContext.otpInfo.suspense.toString(), {
                        thousandSeparator: true,
                        prefix: '$',
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      })}
                    </p>
                    <p className={Styles.label}>Processing Fee: </p>
                    <p>
                      {numericFormatter(oneTimePaymentContext.otpInfo.processingFee.toString(), {
                        thousandSeparator: true,
                        prefix: '$',
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      })}
                    </p>
                    <p className={Styles.label}>Process Date: </p>
                    <p>{dayjs.unix(oneTimePaymentContext.otpInfo?.processDate).format('MM/DD/YYYY')}</p>
                    <p className={Styles.label}>Total Payment: </p>
                    <p>
                      {numericFormatter(
                        (
                          oneTimePaymentContext.totalWithoutFee + oneTimePaymentContext.otpInfo.processingFee
                        ).toString(),
                        {
                          thousandSeparator: true,
                          prefix: '$',
                          decimalScale: 2,
                          fixedDecimalScale: true,
                        },
                      )}
                    </p>
                    {oneTimePaymentContext.routingError && (
                      <>
                        <p className={Styles.label}>Error: </p>
                        <p>{oneTimePaymentContext.routingErrorMessage}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className={Styles.text}>
                <br />
                <p>
                  Do you authorize the payment of{' '}
                  {numericFormatter(
                    (oneTimePaymentContext.totalWithoutFee + oneTimePaymentContext.otpInfo.processingFee).toString(),
                    {
                      thousandSeparator: true,
                      prefix: '$',
                      decimalScale: 2,
                      fixedDecimalScale: true,
                    },
                  )}{' '}
                  for {dayjs.unix(oneTimePaymentContext.otpInfo?.processDate).format('MM/DD/YYYY')} from{' '}
                  {oneTimePaymentContext.autopayInfo.otpBankName} from your bank account ending in{' '}
                  {oneTimePaymentContext.autopayInfo.otpBankAccountNo.toString().slice(-4)}?
                </p>
              </div>
            </div>
          )
        }
        footer={
          pageNumber === 1 ? (
            <div className={Styles.footer}>
              <Button variant='outlined' onClick={closeConfirmModal} className={Styles.cancelButton}>
                Cancel
              </Button>
              <Button
                variant='contained'
                disabled={!isAuthorized}
                onClick={() => setPageNumber((prev) => prev + 1)}
                className={Styles.button}
              >
                Next
              </Button>
            </div>
          ) : (
            <div className={Styles.footer}>
              <Button variant='outlined' onClick={closeConfirmModal} className={Styles.cancelButton}>
                Cancel
              </Button>
              {oneTimePaymentContext.serviceManager && oneTimePaymentContext.routingError && (
                <Button
                  variant='contained'
                  onClick={(e) => oneTimePaymentContext.handleSubmit(e)}
                  className={Styles.button}
                >
                  Ignore Error?
                </Button>
              )}
              {!oneTimePaymentContext.routingError && (
                <Button
                  variant='contained'
                  onClick={(e) => oneTimePaymentContext.handleSubmit(e)}
                  className={Styles.button}
                >
                  Confirm
                </Button>
              )}
            </div>
          )
        }
      />
    );
  }

  if (props.content === 'delete') {
    return (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Delete Payment</h1>}
        content={
          <div className={`${Styles.modalContainer} ${Styles.deleteModalContainer}`}>
            <div>
              Are you sure you want to delete this payment for{' '}
              {dayjs.unix(oneTimePaymentContext.deleteRow.dateReceived).format('MM/DD/YYYY')} with total{' '}
              {numericFormatter(oneTimePaymentContext.deleteRow.amount.toString(), {
                thousandSeparator: true,
                prefix: '$',
                decimalScale: 2,
                fixedDecimalScale: true,
              })}
              ?
            </div>
            <br />
            {!isServicerView && (
              <>
                <div className={Styles.deleteWarning}>
                  Deleting payments may lead to late fees or negative credit reporting. If you're having difficulty
                  making your payments, please contact our Servicing Department at (800) 345-7884 or email
                  CustomerService@swmc.com for assistance.
                </div>
                <br />
              </>
            )}
            <p>This action cannot be undone.</p>
            {/* <br /> */}
          </div>
        }
        footer={
          <div className={`${Styles.footer} ${Styles.deleteModalFooter}`}>
            <Button
              variant='outlined'
              onClick={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
              className={Styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={(e) => oneTimePaymentContext.handleRemoveTransaction(e, deleteRow)}
              className={Styles.button}
            >
              Delete Payment
            </Button>
          </div>
        }
      />
    );
  }

  if (props.content === 'stopCodeST02') {
    return isServicerView ? (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Stop Code ST02</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              OTPs are not permitted due to Certified Funds restriction. Do you have approval to remove the Stop Code
              and setup the OTP?
            </div>
            <br />
            <div className={Styles.warningText}>Remove ST02 from the loan?</div>
          </div>
        }
        footer={
          <div className={Styles.footer}>
            <Button
              variant='outlined'
              onClick={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
              className={Styles.cancelButton}
            >
              NO
            </Button>
            <Button
              variant='contained'
              onClick={() => oneTimePaymentContext.handleRemoveStopCode('ST02')}
              className={Styles.button}
            >
              YES
            </Button>
          </div>
        }
      />
    ) : (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Cannot Accept Payment</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              We are unable to accept online payments due to prior reversals caused by non-sufficient funds. Payments
              can only be accepted through certified funds, i.e., Money Order, Cashier's Check, or Western Union Quick
              Collect.
            </div>
          </div>
        }
      />
    );
  }

  if (props.content === 'stopCodeST03') {
    return isServicerView ? (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Stop Code ST03</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>Unable to accept OTP due to ST03</div>
          </div>
        }
      />
    ) : (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Cannot Accept Payment</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              We are unable to accept online payments at this time. Please contact CustomerService@swmc.com for
              assistance.
            </div>
          </div>
        }
      />
    );
  }

  if (props.content === 'stopCodeST20') {
    return isServicerView ? (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Stop Code ST20</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              Payments are being accepted only through Certified Funds due to issuance of the NOI. Do you have approval
              from the Servicing Team Lead to set up the OTP and remove Certified Funds stop code ST20?
            </div>
            <br />
            <div className={Styles.warningText}>Remove ST20 from the loan?</div>
          </div>
        }
        footer={
          <div className={Styles.footer}>
            <Button
              variant='outlined'
              onClick={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
              className={Styles.cancelButton}
            >
              NO
            </Button>
            <Button
              variant='contained'
              onClick={(e) => oneTimePaymentContext.handleRemoveStopCode('ST20')}
              className={Styles.button}
            >
              YES
            </Button>
          </div>
        }
      />
    ) : (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Cannot Accept Payment</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              We are unable to accept online payments at this time. Please contact CustomerService@swmc.com for
              assistance.
            </div>
          </div>
        }
      />
    );
  }

  if (props.content === 'statusForeclosure') {
    return isServicerView ? (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Loan Status Foreclosure</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>One Time Payments cannot be accepted on loans in Foreclosure.</div>
          </div>
        }
      />
    ) : (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Cannot Accept Payment</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              We are unable to accept online payments at this time. Please contact CustomerService@swmc.com for
              assistance.
            </div>
          </div>
        }
      />
    );
  }

  if (props.content === 'statusDeedInLieu') {
    return isServicerView ? (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Loan Status Deed-In-Lieu</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>One Time Payments cannot be accepted on loans in Deed-In-Lieu.</div>
          </div>
        }
      />
    ) : (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Cannot Accept Payment</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              We are unable to accept online payments at this time. Please contact CustomerService@swmc.com for
              assistance.
            </div>
          </div>
        }
      />
    );
  }

  if (props.content === 'statusShortSale') {
    return isServicerView ? (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Loan Status Short Sale</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>One Time Payments cannot be accepted on loans in Short Sale.</div>
          </div>
        }
      />
    ) : (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Cannot Accept Payment</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              We are unable to accept online payments at this time. Please contact CustomerService@swmc.com for
              assistance.
            </div>
          </div>
        }
      />
    );
  }

  if (props.content === 'statusServicingTransfer') {
    return isServicerView ? (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Loan Status Servicing Transfer</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              One Time Payments cannot be accepted because this loan has been Service Released.
            </div>
          </div>
        }
      />
    ) : (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Cannot Accept Payment</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              We are unable to accept online payments because your loan has been transferred to another Servicer to
              process your payments. Please contact CustomerService@swmc.com for assistance.
            </div>
          </div>
        }
      />
    );
  }

  if (props.content === 'removeBankDetails') {
    return (
      <DefaultModal
        open={oneTimePaymentContext.oneTimePaymentModalOpen}
        onClose={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
        title={<h1>Remove Bank Details?</h1>}
        content={
          <div className={Styles.stopCodeModalContainer}>
            <div className={Styles.stopCodeText}>
              Are you sure you want to clear your bank details for One Time Payments? This will clear routing number,
              bank account name, bank account number, and bank account type from the loan.
            </div>
            <br />
            <div className={Styles.stopCodeText}>This will not affect previously scheduled transactions.</div>
          </div>
        }
        footer={
          <div className={Styles.footer}>
            <Button
              variant='outlined'
              onClick={() => oneTimePaymentContext.setOneTimePaymentModalOpen(false)}
              className={Styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={() => oneTimePaymentContext.handleRemoveBankDetails()}
              className={Styles.button}
            >
              Confirm
            </Button>
          </div>
        }
      />
    );
  }
};

export default OneTimePaymentModal;
